import { AgentInLenderContext, Lender, MerchantStructure } from '@nemuru-eng/js-api-client';
import { Dispatch, SetStateAction, useMemo } from 'react';

import { AddOrEditAgentInLenderContextCredentialsForm } from '@/components/modules/AgentDetail/ProvidersDetails/components/Credentials/components/AgentInLenderContextTable/components/AddOrEditAgentInLenderContextCredentialsDialog/components/AddOrEditAgentInLenderContextCredentialsForm';
import { useAddOrEditAgentInLenderContext } from '@/components/modules/AgentDetail/ProvidersDetails/components/Credentials/components/AgentInLenderContextTable/hooks/useAddOrEditAgentInLenderContext';
import {
  AddAgentInLenderContextCredentialsFormSchema,
  AddAgentInLenderContextCredentialsFormType,
} from '@/components/modules/AgentDetail/ProvidersDetails/components/Credentials/components/AgentInLenderContextTable/schemas/AddAgentInLenderContextCredentialsFormSchema';
import { hasChannelOrAgentInLenderContext } from '@/components/modules/AgentDetail/ProvidersDetails/components/Credentials/constants/storedCredentialFieldFlags';
import { EditDialogWrapper } from '@/components/shared/components/EditDialogWrapper';
interface AddOrEditAgentInLenderContextCredentialsDialogProps {
  opened: boolean;
  setOpened: Dispatch<SetStateAction<boolean>>;
  toggleDialog;
  agentId: string;
  channelId: string;
  lenders: Lender[];
  merchantStructure: MerchantStructure;
  selectedAgentInLenderContext?: AgentInLenderContext;
}
export const AddOrEditAgentInLenderContextCredentialsDialog = ({
  opened,
  setOpened,
  toggleDialog,
  agentId,
  channelId,
  lenders,
  merchantStructure,
  selectedAgentInLenderContext,
}: AddOrEditAgentInLenderContextCredentialsDialogProps) => {
  const { agent, createOrUpdateAgentInLenderContext, loading } = useAddOrEditAgentInLenderContext({
    agentId,
    selectedAgentInLenderContext,
  });

  const currentChannel = merchantStructure.merchant.channels.find((channel) => channel.id === channelId);
  const currentAgent = currentChannel?.agents.find((channelAgent) => channelAgent.id === agent?.id);

  const onSubmit = async (formValues: AddAgentInLenderContextCredentialsFormType) => {
    await createOrUpdateAgentInLenderContext(formValues);
    toggleDialog();
  };

  const shouldPrefillChannelAndAgent = hasChannelOrAgentInLenderContext(selectedAgentInLenderContext);

  const editAgentInLenderContextCredentialsDefaultValues = useMemo(() => {
    if (!selectedAgentInLenderContext) {
      return undefined;
    }

    return {
      lenderId: selectedAgentInLenderContext.lenderId,
      product: selectedAgentInLenderContext.product,
      program: selectedAgentInLenderContext.program,
      merchantIdInLenderContext: selectedAgentInLenderContext.merchantIdInLenderContext,
      market: selectedAgentInLenderContext.market,
      username: null,
      channelIdInLenderContext:
        selectedAgentInLenderContext.channelIdInLenderContext ??
        (shouldPrefillChannelAndAgent ? currentChannel?.description ?? null : null),
      agentIdInLenderContext:
        selectedAgentInLenderContext.agentIdInLenderContext ??
        (shouldPrefillChannelAndAgent ? currentAgent?.name ?? null : null),
      clientSecret: selectedAgentInLenderContext.hasClientSecret ? '' : null,
      clientId: selectedAgentInLenderContext.hasClientId ? '' : null,
      password: selectedAgentInLenderContext.hasPassword ? '' : null,
      articleType: selectedAgentInLenderContext.articleType ?? null,
    };
  }, [selectedAgentInLenderContext, shouldPrefillChannelAndAgent, currentChannel?.description, currentAgent?.name]);

  return (
    <EditDialogWrapper
      opened={opened}
      setOpened={setOpened}
      handleClose={toggleDialog}
      DialogProps={{
        maxWidth: 'sm',
        fullWidth: true,
      }}
      title={selectedAgentInLenderContext ? 'EDIT_CREDENTIALS' : 'ADD_CREDENTIALS'}
      onSubmitForm={onSubmit}
      isLoading={loading}
      defaultValues={editAgentInLenderContextCredentialsDefaultValues}
      formSchema={AddAgentInLenderContextCredentialsFormSchema}
    >
      <AddOrEditAgentInLenderContextCredentialsForm
        agent={agent}
        lenders={lenders}
        currentAgent={currentAgent}
        currentChannel={currentChannel}
        selectedAgentInLenderContext={selectedAgentInLenderContext}
        isEditable={true}
      />
    </EditDialogWrapper>
  );
};
